import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

import {
  VERTICAL_MILESTONE_BAR_ROW_HEIGHT_PX,
  VERTICAL_MILESTONE_BAR_TRACK_WIDTH_PX,
} from '@/modules/players/v3/constants/achievements';
import type { AchievementMilestone } from '@/modules/players/v3/types/achievements';

interface VerticalMilestoneBarProps {
  milestones: AchievementMilestone[];
  isCompleted: boolean;
}

export function VerticalMilestoneBar({ milestones, isCompleted }: VerticalMilestoneBarProps) {
  const rowH = VERTICAL_MILESTONE_BAR_ROW_HEIGHT_PX;
  const N = milestones.length;
  const lastAchievedIndex = milestones.reduce((last, m, i) => (m.achieved ? i : last), -1);
  const totalH = N * rowH;
  const fillH = lastAchievedIndex > 0 ? lastAchievedIndex * rowH : 0;

  return (
    <div className='flex gap-3 pb-2'>
      <div
        className='relative shrink-0'
        style={{ width: VERTICAL_MILESTONE_BAR_TRACK_WIDTH_PX, height: totalH }}
      >
        <div
          className='absolute inset-x-0 bg-gray-200 rounded-full'
          style={{ top: rowH / 2, bottom: rowH / 2 }}
        />
        {fillH > 0 ? (
          <div
            className='absolute inset-x-0 bg-success-200 rounded-full transition-all duration-300'
            style={{ top: rowH / 2, height: fillH }}
          />
        ) : null}
        {milestones.map((milestone, i) => (
          <div
            key={milestone.displayOrder}
            className='absolute left-1/2'
            style={{ top: i * rowH + rowH / 2, transform: 'translateX(-50%) translateY(-50%)' }}
          >
            <FontAwesomeIcon
              icon={faCircleCheck}
              className={clsx('size-5', milestone.achieved ? 'text-success-400' : 'text-gray-300')}
            />
          </div>
        ))}
      </div>

      <div className='flex-1 flex flex-col'>
        {milestones.map((milestone) => (
          <div
            key={milestone.displayOrder}
            className='flex flex-col justify-center'
            style={{ height: rowH }}
          >
            <span
              className={clsx('text-sm font-semibold', {
                'text-brand-700': isCompleted,
                'text-gray-900': !isCompleted && milestone.achieved,
                'text-gray-500': !isCompleted && !milestone.achieved,
              })}
            >
              {`Milestone ${milestone.displayOrder}`}
            </span>
            <span className='text-sm text-gray-500'>{milestone.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
